export type TierId = 'plus' | 'max' | 'ultra';
export type DurationId = 'monthly' | 'quarterly' | 'yearly';

export interface TierPlan {
  tier: TierId;
  label: string;
  usageLimit: number;
  tokenLimit: number;
  windowHours: number;
  description: string;
  highlight?: boolean;
}

export interface DurationOption {
  id: DurationId;
  label: string;
  durationDays: number;
}

export interface TierPricing {
  tier: TierId;
  duration: DurationId;
  price: number;
}

export const TIERS: TierPlan[] = [
  { tier: 'plus',  label: 'Plus',  usageLimit: 25,  tokenLimit: 1_500_000, windowHours: 5, description: '25 messages & 1.5M tokens every 5 hours' },
  { tier: 'max',   label: 'Max',   usageLimit: 100, tokenLimit: 6_000_000, windowHours: 5, description: '100 messages & 6M tokens every 5 hours', highlight: true },
  { tier: 'ultra', label: 'Ultra', usageLimit: 200, tokenLimit: 10_000_000, windowHours: 5, description: '200 messages & 10M tokens every 5 hours' },
];

export const DURATIONS: DurationOption[] = [
  { id: 'monthly',   label: 'Monthly',   durationDays: 30  },
  { id: 'quarterly', label: 'Quarterly', durationDays: 90  },
  { id: 'yearly',    label: 'Yearly',    durationDays: 365 },
];

export const TIER_PRICING: TierPricing[] = [
  { tier: 'plus',  duration: 'monthly',   price: 20   },
  { tier: 'plus',  duration: 'quarterly', price: 54 },
  { tier: 'plus',  duration: 'yearly',    price: 192  },
  { tier: 'max',   duration: 'monthly',   price: 70  },
  { tier: 'max',   duration: 'quarterly', price: 187  },
  { tier: 'max',   duration: 'yearly',    price: 672 },
  { tier: 'ultra', duration: 'monthly',   price: 180   },
  { tier: 'ultra', duration: 'quarterly', price: 481   },
  { tier: 'ultra', duration: 'yearly',    price: 1728  },
];

export function getPrice(tier: TierId, duration: DurationId): number {
  const pricing = TIER_PRICING.find(p => p.tier === tier && p.duration === duration);
  if (!pricing) throw new Error(`Missing subscription price: ${tier}/${duration}`);
  return pricing.price;
}

const DURATION_MONTHS: Record<DurationId, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
};

/** Savings percent (integer) of a duration vs paying monthly for the same span. 0 for monthly. */
export function getMonthlyDiscountPercent(tier: TierId, duration: DurationId): number {
  const monthlyTotal = getPrice(tier, 'monthly') * DURATION_MONTHS[duration];
  return Math.round((1 - getPrice(tier, duration) / monthlyTotal) * 100);
}

export function getTier(tierId: TierId): TierPlan | undefined {
  return TIERS.find(t => t.tier === tierId);
}
